import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  GetOrdersDocument,
  useCheckEsignatureContractStatusMutation,
} from '@src/generated/graphql';
import { useRequiredUserId } from '@src/hooks/useRequiredUserId';
import { useToast } from '@src/hooks/useToast';
import { logAndFormatError } from '@src/services/error-formatter';

export function EsignatureSuccessChecker(): JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldCheckContract = searchParams.get('check_esignature_contract');
  const contractType = searchParams.get('contract_type');
  const contractId = searchParams.get('esio_contract_id');
  const toast = useToast();
  const { t } = useTranslation();
  const userId = useRequiredUserId();

  const [checkEsignature] = useCheckEsignatureContractStatusMutation();

  const toastShown = useRef(false);

  useEffect(() => {
    if (
      !shouldCheckContract ||
      !contractId ||
      !contractType ||
      toastShown.current
    ) {
      return;
    }
    checkEsignature({
      variables: { input: { contractId } },
      refetchQueries: [{ query: GetOrdersDocument, variables: { userId } }],
    }).catch((err) => {
      toast.error(
        logAndFormatError(
          err,
          t(
            'dashboard.esignature.check_error',
            'Unable to check contract status. Please refresh your page.',
          ),
        ),
      );
    });
    setSearchParams({});
    if (contractType === 'fiscal_rep_cancellation') {
      toast.success(
        t(
          'dashboard.esignature.fiscal_rep_cancellation_success',
          'Your fiscal representation has been cancelled.',
        ),
      );
    }
    toastShown.current = true;
  }, [
    checkEsignature,
    shouldCheckContract,
    contractType,
    contractId,
    toast,
    setSearchParams,
    t,
    userId,
  ]);

  return null;
}
